import React from 'react';

import { API, Logger, graphqlOperation } from 'aws-amplify';
import { getExecutionInput } from '../../../graphql/queries';
import diff from 'deep-diff';

//import { inlineJsonProps } from '../../utils/helpers';

const logger = new Logger('useExecutionInputs');

const listExecutionInputs = `
  query ListExecutionInputs(
    $filter: _ExecutionInputFilter
    $limit: Int
    $order: SortDirection
    $orderBy: ExecutionInputOrderBy
    $nextToken: String
  ) {
    listExecutionInputs(filter: $filter, order: $order, orderBy: $orderBy, limit: $limit, nextToken: $nextToken) {
      items {
        Attempts
        ClientAccountId
        EndTime
        ExecutionId
        InputSizeInBytes
        InputUri
        LogStream
        Metrics
        Outputs
        Result
        ResultSummary
        SourceDatasetId
        Status
        SubmitTime
        UpdateTime
        WorkloadId
      }
      nextToken
    }
  }
`;

const onMutateExecutionInput = `
  subscription OnMutateExecutionInput[args1] {
    onMutateExecutionInput[args2] {
      mutationType
      clientAccountId
      executionId
      workloadId
      payload
    }
  }
`;

function resolveJson(data) {
  if(data) {
    ["Attempts", "Metrics", "Outputs"].forEach( (elem) => {
      if(data.hasOwnProperty(elem)) {
        data[elem]=JSON.parse(data[elem]);
      }
    });
  }
  return data;
}

function executionInputsReducer(state, action) {
  switch (action.type) {
    case "INIT":
      const reset=(state.withResult !== action.withResult) || 
                  (state.withStatus !== action.withStatus);
      return { 
        ...state,
        executionInputs: reset?[]:state.executionInputs,
        error: false,
        withStatus: action.withStatus,
        withResult: action.withResult,
      };
    case "LOAD":
      logger.info("LOAD: ", action.chunk);
      const newExecutionInputs=(action.chunk.items?action.chunk.items:[]).map( (e) => resolveJson(e) );
      return { 
        ...state,
        executionInputs: [...state.executionInputs, ...newExecutionInputs],
        nextToken: action.chunk.nextToken?action.chunk.nextToken:null,
      };
    case "LOADING":
      return { 
        ...state,
        loading: action.status,
      };
    case "ERROR":
      return { 
        ...state,
        error: action.message,
      };
    case "INSERT":
      let executionInput = JSON.parse(action.mutation.payload);
      executionInput['ClientAccountId']=action.mutation.clientAccountId;
      executionInput['WorkloadId']=action.mutation.workloadId;
      executionInput['ExecutionId']=action.mutation.executionId;
      executionInput['InputUri']=action.mutation.inputUri;
      logger.info('INSERT',executionInput);
      return { 
        ...state,
        executionInputs: [...state.executionInputs, executionInput],
      };
    case "MODIFY":
      const differences = JSON.parse(action.mutation.payload);
      const updatedExecutionInputs = state.executionInputs.map(executionInput =>
        {
          if (executionInput.ExecutionId === action.mutation.executionId && 
              executionInput.InputUri === action.mutation.inputUri) {
            const updatedExecutionInput = JSON.parse(JSON.stringify(executionInput));
            differences.forEach((change) => {
               diff.applyChange(updatedExecutionInput,null,change); 
            });
            logger.info('MODIFY',executionInput, differences, updatedExecutionInput);
            return updatedExecutionInput;
          } else {
            return executionInput;
          }
        });
      return { 
        ...state,
        executionInputs: updatedExecutionInputs,
      };
    case "MUTATE":
      logger.debug('MUTATE',action);
      return executionInputsReducer(state, {
        type: action.mutation.mutationType,
        mutation: action.mutation,
      }); // INSERT, MODIFY or REMOVE
    case "REMOVE":
      logger.info('REMOVE',action.mutation.executionId);
      const prunedExecutionInputs = state.executionInputs.filter(
        executionInput => executionInput.ExecutionId !== action.mutation.executionId &&
                          executionInput.InputUri !== action.mutation.inputUri);
      return { 
        ...state,
        executions: prunedExecutionInputs,
      };
    default:
      logger.error('UNKNOWN:',action.type);
      return state;
  }
}

function fmtQueryArgs(query, args1, args2) {
  const queryArgs1=query.replace('[args1]',args1);
  const queryArgs2=queryArgs1.replace('[args2]',args2);
  return queryArgs2;
}

export const useExecutionInputs = (props) => {

  const [state, dispatch] = React.useReducer(executionInputsReducer, { 
    executionInputs: [], 
    loading: false,
    error: null,
    nextToken: null,
    withStatus: null,
    withResult: null,
  });
  
  const { 
    clientAccountId, workloadId, executionId, 
    orderBy, order, withResult, withStatus, subscribed, 
    loadMoreInputs, limit=2000 } = props;
    
  const fetchExecutionInputs = async (variables) => {
    
    let hasMore=true;
    let maxRows=variables.limit;
    let retrievedRows=0;
    let nextToken=null;
    
    dispatch({ type: 'LOADING', status: true});
    dispatch({ type: 'ERROR', message: null});

    while(hasMore && retrievedRows<maxRows) {
      if(nextToken) {
        variables['nextToken']=nextToken;
      }

      try {
        const response = await API.graphql(graphqlOperation(listExecutionInputs,variables));
        if(response.errors) {
          dispatch({ type: 'ERROR', message: response.errors[0].message});
        } else {
          const data = response.data.listExecutionInputs;
          if(data.items) {
            dispatch({ type: 'LOAD', chunk: data });
            retrievedRows+=data.items.length;
          }
          if(data.nextToken) {
            nextToken=data.nextToken;
          } else {
            hasMore=false;
          }
        }
      } catch(error) {
        dispatch({ type: 'ERROR', message: error});
        hasMore=false;
      }
    }
    
    dispatch({ type: 'LOADING', status: false});
    
  };

  React.useEffect(() => {
    let variables = null;
    if (clientAccountId || workloadId || executionId) {
      variables={
        filter: {}
      };
      if(clientAccountId) {
        variables.filter['ClientAccountId'] = { eq: clientAccountId };
      }
      if(workloadId) {
        variables.filter['WorkloadId'] = { eq: workloadId };
      }
      if(executionId) {
        variables.filter['ExecutionId'] = { eq: executionId };
      }
      if(withResult) {
        variables.filter['Result'] = { eq: withResult };
      }
      if(withStatus) {
        variables.filter['Status'] = { eq: withStatus };
      }
      if(orderBy) {
        variables.orderBy = orderBy;
      }
      if(order) {
        variables.order = order;
      }
      if(limit) {
        variables.limit = limit;
      }
      if(loadMoreInputs) {
        variables.nextToken = loadMoreInputs;
      }
    }

    dispatch({ type: 'INIT', withResult, withStatus });

    fetchExecutionInputs(variables);

    let subscription = null;
    const mutationType = (subscribed&&['INSERT', 'MODIFY', 'REMOVE'].indexOf(subscribed) >= 0)?subscribed:null;

    let subscriptionVariables = null;
    let subscriptionArgs1 = "";
    let subscriptionArgs2 = "";
    let args1 = [];
    let args2 = [];
    if (mutationType || clientAccountId || workloadId || executionId) {
      subscriptionVariables={};
      if (mutationType) {
        subscriptionVariables['MutationType'] = mutationType;
        args1.push('$MutationType: ID');
        args2.push('mutationType: $MutationType');
      }
      if (clientAccountId) {
        subscriptionVariables['ClientAccountId'] = clientAccountId;
        args1.push('$ClientAccountId: ID');
        args2.push('clientAccountId: $ClientAccountId');
      }
      if (workloadId) {
        subscriptionVariables['WorkloadId'] = workloadId;
        args1.push('$WorkloadId: ID');
        args2.push('workloadId: $WorkloadId');
      }
      if (executionId) {
        subscriptionVariables['ExecutionId'] = executionId;
        args1.push('$ExecutionId: ID');
        args2.push('executionId: $ExecutionId');
      }
      subscriptionArgs1="(" + args1.join(',') + ")";
      subscriptionArgs2="(" + args2.join(',') + ")";
    }
    
    if(subscribed) {
      const subscribeOperation=fmtQueryArgs(onMutateExecutionInput,subscriptionArgs1,subscriptionArgs2);
      subscription=API.graphql(graphqlOperation(subscribeOperation,subscriptionVariables)).subscribe({
        next: data => {
          const {
            value: {
              data: { onMutateExecutionInput }
            }
          } = data;
          dispatch({
            type: 'MUTATE',
            mutation: onMutateExecutionInput,
          });
        }
      });
    }

    return () => { 
      logger.debug("useExecutionInputs cleanup!");
      if(subscription) {
        subscription.unsubscribe();
      }
    };

  }, [clientAccountId, workloadId, executionId, orderBy, order, withResult, withStatus, subscribed, loadMoreInputs, limit]);
  
  return { 
    inputs: state.executionInputs, 
    loading: state.loading, 
    error: state.error,
    moreInputs: state.nextToken,
  };
  
};

function executionInputReducer(state, action) {
  switch (action.type) {
    case "SET":
      logger.info("SET: ", action.executionInput);
      return { 
        ...state,
        executionInput: resolveJson(action.executionInput),
      };
    case "LOADING":
      return { 
        ...state,
        loading: action.status
      };
    case "ERROR":
      return { 
        ...state,
        error: action.message 
      };
    case "MODIFY":
      const differences=JSON.parse(action.mutation.payload);
      const updatedExecutionInput=JSON.parse(JSON.stringify(state.executionInput));
      differences.forEach((change) => {
         diff.applyChange(updatedExecutionInput,null,change); 
      });
      logger.info('MODIFY',state.executionInput, differences, updatedExecutionInput);
      return { 
        ...state,
        executionInput: updatedExecutionInput,
      };
    default:
      logger.error('UNKNOWN:',action.type);
      return state;
  }
}

export const useExecutionInput = (executionId, inputUri, options={ subscribed: false }) => {

  const [state, dispatch] = React.useReducer(executionInputReducer, { 
    executionInput: null, 
    loading: false,
    error: null
  });

  const { subscribed } = options;
  
  React.useEffect(() => {
    let unmounted = false;
    dispatch({ type: 'LOADING', status: true});
    dispatch({ type: 'ERROR', message: null});
    const variables = {
      executionId: executionId,
      inputUri: inputUri
    };

    API.graphql(graphqlOperation(getExecutionInput,variables))
      .then(res => res.data.getExecutionInput)
      .then(item => {
        if(!unmounted) {
          dispatch({ type: 'LOADING', status: false});
          dispatch({ type: 'SET', executionInput: item});
        }
      })
      .catch(err => {
        if(!unmounted) {
          dispatch({ type: 'LOADING', status: false});
          dispatch({ type: 'ERROR', message: err});
        }
      });

    let subscription = null;

    let subscriptionVariables = {ExecutionId: executionId, InputUri: inputUri};
    let args1 = ["$ExecutionId: ID", "$InputUri: ID"];
    let args2 = ["executionId: $ExecutionId", "inputUri: $InputUri"];
    const subscriptionArgs1=args1.length?"(" + args1.join(',') + ")":"";
    const subscriptionArgs2=args2.length?"(" + args2.join(',') + ")":"";

    if(subscribed) {
      logger.info("Subscribing execution input");
      const subscribeOperation=fmtQueryArgs(onMutateExecutionInput,subscriptionArgs1,subscriptionArgs2);
      subscription=API.graphql(graphqlOperation(subscribeOperation,subscriptionVariables)).subscribe({
        next: data => {
          const {
            value: {
              data: { onMutateExecutionInput }
            }
          } = data;
          dispatch({ type: 'MODIFY', mutation: onMutateExecutionInput});
        }
      });
    }
    
    return () => { 
      unmounted = true;
      if(subscription) {
        logger.info("Unsubscribing execution input");
        subscription.unsubscribe();
      }
    };

  }, [executionId, inputUri, subscribed]);
  
  return { 
    executionInput: state.executionInput,
    loading: state.loading,
    error: state.error,
  };
};

